import { useState } from 'react';
import { Localized } from '@fluent/react';
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../../app/hooks';
import { softDeleteDocument } from '../documentsApi';
import { showError, showSuccess } from '../../notifications/notificationsSlice';
import { DeleteButton } from '../../../app/components/DeleteButton';
import { CancelButton } from '../../../app/components/CancelButton';

interface DocumentSoftDeleteDialogProps {
    open: boolean;
    documentId: string;
    documentNo?: string;
    onClose: () => void;
    onSuccess?: () => void;
}

export const DocumentSoftDeleteDialog = ({ open, documentId, documentNo, onClose, onSuccess }: DocumentSoftDeleteDialogProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [deleting, setDeleting] = useState(false);

    const handleCancel = () => {
        if (!deleting) {
            setPassword('');
            onClose();
        }
    };

    const handleConfirm = async () => {
        if (!password) {
            return;
        }

        setDeleting(true);
        try {
            const result = await dispatch(softDeleteDocument({
                documentId,
                password,
            }));

            if (softDeleteDocument.fulfilled.match(result)) {
                dispatch(showSuccess('deleted'));
                setPassword('');
                onClose();

                if (onSuccess) {
                    onSuccess();
                } else {
                    navigate(-1);
                }
            } else {
                dispatch(showError('error'));
            }
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="xs">
            <DialogTitle>
                <Localized id="delete"><span>_Usuń dokument</span></Localized>
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
                <Alert severity="warning" sx={{ mb: 2, mt: 1 }}>
                    <Typography variant="body2">
                        <Localized id="confirm-soft-doc-delete-params" vars={{ documentNo: documentNo ?? '-' }}>
                            <span>_
                                Dokument {documentNo ? `"${documentNo}"` : ''} zostanie usunięty. Czy na pewno chcesz kontynuować?
                            </span>
                        </Localized>
                    </Typography>
                </Alert>

                <TextField
                    autoFocus
                    fullWidth
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    label={<Localized id="password"><span>_Hasło</span></Localized>}
                />
            </DialogContent>
            <DialogActions>
                <DeleteButton loading={deleting} onClick={handleConfirm} />
                <CancelButton loading={deleting} onClick={handleCancel} />
            </DialogActions>
        </Dialog>
    );
};
